import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getRoles, deleteRole } from '../services/iam';
import { Role } from '../types/iam';
import { toast } from '@/lib/toast';
import RoleFormModal from '../components/modals/RoleFormModal';
import { AlertTriangle, IdCard, Pencil, Plus, RefreshCw, SearchX, Trash2 } from 'lucide-react';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import PermissionGate from '../components/PermissionGate';
import { usePermissions } from '../hooks/usePermissions';
import { Button, Card, Checkbox, Chip, EmptyState, IconButton, PageHeader, PaginationBar, SearchField, TanstackTable } from '@/components/ds-zta';
import { useDialogs } from '@/components/dialogs/DialogProvider';
import { useUrlState } from '@/lib/useUrlState';
import { useDebouncedValue } from '@/lib/useDebouncedValue';
import { ROWS_PER_PAGE_OPTIONS, pageOffset, readPage, readRows } from '@/lib/urlPaging';

const columnHelper = createColumnHelper<Role>();

const RolesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { PERMISSIONS } = usePermissions();
  const { confirm: confirmDialog } = useDialogs();
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  // Search, the primary-only filter and paging live in the URL (design eval
  // F24). Both filters are server-side (`q`, `primary_only`), so changing
  // either starts again at page 1 (F7).
  const [url, setUrl] = useUrlState({ q: '', primary: '', page: '1', rows: '20' });
  const page = readPage(url.page);
  const itemsPerPage = readRows(url.rows);
  const primaryOnly = url.primary === '1';
  const setPrimaryOnly = (next: boolean) => setUrl({ primary: next ? '1' : null, page: null });
  const q = useDebouncedValue(url.q.trim());

  const { data: rolesResponse, isLoading, isError, error, refetch } = useQuery(
    // ['roles', 'list', …] is this page's paged list; ['roles', 'all'] and
    // ['roles', 'primary'] are the unpaged lookups the user screens use.
    // invalidateQueries('roles') refreshes all three.
    ['roles', 'list', q, page, itemsPerPage, primaryOnly],
    async () => {
      const response = await getRoles({ q: q || undefined, limit: itemsPerPage, offset: pageOffset(page, itemsPerPage), primary_only: primaryOnly });
      return response;
    },
    { keepPreviousData: true }
  );

  const deleteMutation = useMutation(deleteRole, {
    onSuccess: () => {
      queryClient.invalidateQueries('roles');
      toast.success('Role deleted successfully');
    },
    onError: (err: any) => {
      toast.error(`Failed to delete role: ${err.message}`);
    },
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        meta: { sortable: true, sortValue: (r) => (r.name || '').toLowerCase() },
        cell: (info) => {
          const role = info.row.original;
          return (
            <div className="flex items-center gap-2">
              <Link
                to={`/roles/${role.id}`}
                className="text-dense font-medium text-[var(--d2d-text-primary)] hover:underline"
              >
                {info.getValue()}
              </Link>
              {role.is_primary && (
                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-[var(--accent-tag-bg)] text-[var(--accent-tag-text)]">
                  Primary
                </span>
              )}
              {role.direct_access && (
                <Chip size="sm" variant="neutral">
                  Direct Access
                </Chip>
              )}
            </div>
          );
        },
      }),
      columnHelper.accessor('key', {
        header: 'Key',
        meta: { sortable: true },
        cell: (info) => (
          <span className="text-dense text-[var(--d2d-text-muted)]">
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor('description', {
        header: 'Description',
        cell: (info) => (
          <span className="text-dense text-[var(--d2d-text-muted)]">
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: () => <span className="block text-right">Actions</span>,
        cell: (info) => {
          const role = info.row.original;
          return (
            <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
              <PermissionGate permission={PERMISSIONS.ROLES_UPDATE}>
                <IconButton
                  size="sm"
                  aria-label="Edit role"
                  tooltip="Edit role"
                  icon={<Pencil className="h-4 w-4" />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingRole(role);
                  }}
                />
              </PermissionGate>
              <PermissionGate permission={PERMISSIONS.ROLES_DELETE}>
                <IconButton
                  size="sm"
                  aria-label="Delete role"
                  tooltip="Delete role"
                  icon={<Trash2 className="h-4 w-4" />}
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (await confirmDialog({
                      title: `Delete ${role.name}?`,
                      message: `"${role.name}" will be permanently deleted. Users and groups bound to it lose the access it grants.`,
                      confirmLabel: 'Delete',
                      variant: 'danger',
                    })) {
                      deleteMutation.mutate(role.id);
                    }
                  }}
                  disabled={deleteMutation.isLoading}
                />
              </PermissionGate>
            </div>
          );
        },
      }),
    ],
    [deleteMutation.isLoading, confirmDialog]
  );

  const roles = rolesResponse?.data || [];
  const total = rolesResponse?.total || 0;

  const table = useReactTable({
    data: roles,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handlePageChange = (newPage: number) => {
    setUrl({ page: newPage });
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setUrl({ rows: newItemsPerPage, page: null });
  };

  const openCreate = () => setCreateModalOpen(true);

  const emptyState = isError ? (
    <EmptyState
      icon={<AlertTriangle />}
      title="Couldn't load roles"
      description={
        (error as any)?.response?.data?.error ||
        (error as any)?.response?.data?.message ||
        (error as any)?.message ||
        'Something went wrong while loading this list.'
      }
      action={
        <Button size="sm" variant="secondary" leadingIcon={<RefreshCw className="h-4 w-4" />} onClick={() => refetch()}>
          Retry
        </Button>
      }
    />
  ) : q ? (
    <EmptyState
      icon={<SearchX />}
      title={`No roles match "${q}"`}
      description={primaryOnly ? 'Only primary roles are shown. Try a different search or show all roles.' : 'Try a different search term.'}
      action={
        <Button size="sm" variant="secondary" onClick={() => setUrl({ q: null, page: null })}>
          Clear search
        </Button>
      }
    />
  ) : primaryOnly ? (
    <EmptyState
      icon={<IdCard />}
      title="No primary roles"
      description="No roles are marked as primary. Clear the filter to see every role."
      action={
        <Button size="sm" variant="secondary" onClick={() => setPrimaryOnly(false)}>
          Show all roles
        </Button>
      }
    />
  ) : (
    <EmptyState
      icon={<IdCard />}
      title="No roles yet"
      description="Roles bundle permissions and resource access. Create one, then assign it to users or groups."
      action={
        <PermissionGate permission={PERMISSIONS.ROLES_CREATE}>
          <Button size="sm" leadingIcon={<Plus className="h-4 w-4" aria-hidden="true" />} onClick={openCreate}>
            Create Role
          </Button>
        </PermissionGate>
      }
    />
  );

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        icon={<IdCard />}
        title="Role Management"
        description="Define roles and their associated permissions."
        primaryAction={
          <PermissionGate permission={PERMISSIONS.ROLES_CREATE}>
            <Button leadingIcon={<Plus className="h-4 w-4" aria-hidden="true" />} onClick={openCreate}>
              Create Role
            </Button>
          </PermissionGate>
        }
      />
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="sm:w-80">
          <SearchField
            label="Search roles"
            placeholder="Search roles…"
            value={url.q}
            onChange={(next) => setUrl({ q: next, page: null }, { replace: true })}
          />
        </div>
        <Checkbox
          id="primary-only"
          checked={primaryOnly}
          onChange={(checked) => setPrimaryOnly(checked)}
          label="Show primary roles only"
        />
      </div>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <TanstackTable aria-label="Roles" table={table} loading={isLoading} empty={emptyState} />
        </div>
      </Card>
      {total > 0 && (
        <div>
          {<PaginationBar
            page={page}
            totalRows={total}
            rowsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleItemsPerPageChange}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />}
        </div>
      )}

      {isCreateModalOpen && (
        <RoleFormModal onClose={() => setCreateModalOpen(false)} />
      )}

      {editingRole && (
        <RoleFormModal
          role={editingRole}
          onClose={() => setEditingRole(null)}
        />
      )}
    </div>
  );
};

export default RolesPage;
